// api/encryptionApi.js
import axios from "axios";
import CryptoJS from "crypto-js";

let TOKEN_KEY = "";
let SECRET_KEY = null;
let IV = null;
let CONFIG = null;
let sessionPromise = null;

let BASE_BACKEND_URL = window.location.origin;

// Single-flight function to init session keys & config from backend exactly once
export async function initSession() {
    if (CONFIG && SECRET_KEY && IV && TOKEN_KEY) {
        return CONFIG;
    }
    if (sessionPromise) {
        return sessionPromise;
    }

    sessionPromise = (async () => {
        try {
            const res = await axios.post(`${BASE_BACKEND_URL}/api/get-token`, {
                params: { url: window.location.href, userAgent: navigator.userAgent }
            });

            const decryptedConfigStr = CryptoJS.AES.decrypt(
                res.data.encryptedData,
                'BUiJ6YioLLCKGgRJdaJZ2NTrunwJOBvf'
            ).toString(CryptoJS.enc.Utf8);

            CONFIG = JSON.parse(decryptedConfigStr);

            if (CONFIG.secret) SECRET_KEY = CryptoJS.enc.Base64.parse(CONFIG.secret);
            if (CONFIG.iv) IV = CryptoJS.enc.Base64.parse(CONFIG.iv);
            if (CONFIG.apiAuth?.token) TOKEN_KEY = CONFIG.apiAuth.token;

            // Store encrypted token in localStorage for legacy HEADER_AUTH usages
            if (CONFIG.apiAuth?.token && CONFIG.apiAuth?.key) {
                const encryptedToken = CryptoJS.AES.encrypt(CONFIG.apiAuth.token, CONFIG.apiAuth.key).toString();
                localStorage.setItem("tkn", encryptedToken);
            }

            return CONFIG;
        } catch (err) {
            sessionPromise = null;
            console.error("Error fetching get-token / session keys:", err);
            throw err;
        }
    })();

    return sessionPromise;
}

// Compatibility helper functions
export const fetchConfig = async () => await initSession();
export const getConfig = () => CONFIG;
export const getGoogleApiKey = () => CONFIG?.googleKey || "";

// Encrypt function
function encryptData(data) {
    if (!SECRET_KEY || !IV) return data;
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY, {
        iv: IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    }).toString();
}

// Decrypt function
function decryptData(encrypted) {
    if (!SECRET_KEY || !IV) return encrypted;
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY, {
        iv: IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
    });
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
}

// Create axios instance for encrypted /api routes
const api = axios.create({
    baseURL: BASE_BACKEND_URL + '/api',
    headers: { "Content-Type": "application/json" },
});

// Request interceptor: ensure session keys, attach token, encrypt payload
api.interceptors.request.use(async (config) => {
    await initSession();
    const token = TOKEN_KEY;
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data && !(config.data instanceof FormData)) {
        config.data = { payload: encryptData(config.data) };
    }
    return config;
});

// Response interceptor: auto decrypt
api.interceptors.response.use((res) => {
    if (res.data?.payload) {
        return decryptData(res.data.payload);
    }
    return res.data;
});

// Intercept global axios to ensure legacy requests wait for token on first visit
axios.interceptors.request.use(async (config) => {
    if (config.url && config.url.includes("/api/get-token")) {
        return config;
    }
    try {
        await initSession();
    } catch (e) {
        // continue if token fetch failed to avoid halting requests completely
    }
    const tkn = localStorage.getItem("tkn");
    if (!config.headers) {
        config.headers = {};
    }
    const authHeader = config.headers.Authorization;
    if (!authHeader || authHeader === "null" || authHeader === "undefined") {
        if (tkn) {
            config.headers.Authorization = tkn;
        }
    }
    return config;
});

// Wrap window.fetch to ensure legacy fetch requests wait for token on first visit
if (typeof window !== "undefined" && window.fetch) {
    const originalFetch = window.fetch;
    window.fetch = async function (input, init = {}) {
        const url = typeof input === "string" ? input : input?.url || "";
        if (url && url.includes("/api/get-token")) {
            return originalFetch.apply(this, arguments);
        }
        try {
            await initSession();
        } catch (e) {
            // continue if initSession fails
        }
        const tkn = localStorage.getItem("tkn");
        if (tkn && init) {
            if (!init.headers) {
                init.headers = {};
            }
            if (typeof init.headers === "object" && !(init.headers instanceof Headers) && !Array.isArray(init.headers)) {
                const authHeader = init.headers.Authorization;
                if (!authHeader || authHeader === "null" || authHeader === "undefined") {
                    init.headers.Authorization = tkn;
                }
            }
        }
        return originalFetch.apply(this, arguments);
    };
}

// Eagerly initiate token retrieval on bundle load
initSession().catch((err) => {
    console.error("Initial get-token call failed:", err);
});

export default api;
